/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, comparer, computed, makeObservable, observable, reaction, runInAction } from 'mobx';

import type { SelectionProps } from '@veeam/components';

import { RequestParams } from '../../../interfaces';
import { AUTO_UPDATE_KEY } from '../../../../constants';
import { SelectionMode } from '../../../enums';
import { RequestSuccessResponse } from '../../../../../interfaces';

import type { FetchMethod, GridProps, RequestParamsForActions, RequestRESTParamsForActions } from '../../../interfaces';
import type { GridStore } from '../grid-store';

export class RequestStore<RowDataT, TotalRowDataT, FilterT> {
    @observable loading = false; // inner store's loading state
    @observable spinner = false; // visual spinner's loading state

    fetchMethod: FetchMethod<RowDataT, FilterT>;
    autoUpdateKey: string;
    loadingDelayTime: number;
    loadingTimeoutId: number | null = null;

    constructor(
        private gridStore: GridStore<RowDataT, TotalRowDataT, FilterT>,
        props: GridProps<RowDataT, TotalRowDataT, FilterT>,
    ) {
        makeObservable(this);

        this.fetchMethod = props.data;
        this.autoUpdateKey = props.autoUpdateKey ?? AUTO_UPDATE_KEY;
        this.loadingDelayTime = props.loadingDelayTime ?? 500;

        reaction(
            () => this.requestParams,
            () => this.reloadGrid(),
            { equals: comparer.structural, fireImmediately: props.autoLoad ?? true }
        );
    }

    @computed
    get requestParams(): RequestParams<FilterT> {
        return {
            start: this.gridStore.pagination.start,
            page: this.gridStore.pagination.activePage,
            limit: this.gridStore.pagination.limit,
            filter: this.gridStore.filters,
            sort: this.gridStore.sortRequestParams,
        };
    }

    @computed
    get requestParamsForActions(): Partial<RequestParamsForActions<FilterT>> {
        return this.getRequestParamsForActions(
            this.gridStore.selectionStore.selection,
            this.gridStore.pageData,
            this.requestParams.filter,
            this.gridStore.selectionStore.selection.field as keyof RowDataT,
            this.gridStore.selectionStore.selected,
            this.gridStore.selectionStore.selectionMode,
        );
    }

    @action.bound
    getCustomRequestParamsForActions(field: keyof RowDataT): Partial<RequestParamsForActions<FilterT>> {
        return this.getRequestParamsForActions(
            this.gridStore.selectionStore.selection,
            this.gridStore.pageData,
            this.requestParams.filter,
            field,
            this.gridStore.selectionStore.selected,
            this.gridStore.selectionStore.selectionMode,
        );
    }

    protected getRequestParamsForActions(
        selection: Partial<SelectionProps<RowDataT>>,
        pageData: RowDataT[],
        filter: FilterT,
        field: keyof RowDataT,
        selected: RowDataT[],
        selectionMode: SelectionMode,
    ): Partial<RequestParamsForActions<FilterT>> {
        // Do NOT use "this" here, only arguments. It will break @computed value based on this method
        let onlyThese;
        let exclude;

        if (selectionMode === SelectionMode.AllPages) {
            const deselectedRows = pageData.filter(row => !selected.includes(row));
            const areThereDeselectedRows = deselectedRows.length > 0;

            onlyThese = selection.isSelectedAll || areThereDeselectedRows ? [] : selected.map(row => row[field]);
            exclude = areThereDeselectedRows ? deselectedRows.map(row => row[field]) : [];
        }

        if (selectionMode === SelectionMode.CurrentPage) {
            onlyThese = selected.map(row => row[field]);
            exclude = [];
        }

        return {
            onlyThese,
            exclude,
            filter,
        };
    }

    @computed
    get requestRESTParamsForActions(): Partial<RequestRESTParamsForActions<FilterT>> {
        const params = this.requestParamsForActions;

        return {
            onlyResourcesWithTheseUids: params.onlyThese,
            excludeResourcesWithTheseUids: params.exclude,
        };
    }

    @action.bound
    setLoading(value: boolean): void {
        this.loading = value;
    }

    @action.bound
    fetchData(): void {
        this.onRequestStart();

        this.sendRequest()
            .then(this.gridStore.sortingStore.doLocalSortingIfRequired)
            .then(this.injectIdsIfRequired)
            .then(this.gridStore.paginationStore.updateData)
            .finally(this.onRequestFinish);
    }

    @action.bound
    reloadGrid(): void {
        this.fetchData();
        this.gridStore.deselectAll();
    }

    sendRequest(autoUpdate = false): Promise<RequestSuccessResponse<RowDataT[]>> {
        return this.fetchMethod({
            ...this.requestParams,
            sort: this.gridStore.sortingStore.modifySortBeforeRequest(this.gridStore.sortRequestParams),
            ...(autoUpdate ? { [this.autoUpdateKey]: true } : {}),
        }) as Promise<RequestSuccessResponse<RowDataT[]>>;
    }

    updateFetchMethod(fetchMethod: FetchMethod<RowDataT, FilterT>): void {
        this.fetchMethod = fetchMethod;
    }

    @action.bound
    injectIdsIfRequired(response: RequestSuccessResponse<RowDataT[]>): RequestSuccessResponse<RowDataT[]> {
        if (this.gridStore.selection.field === this.gridStore.selectionStore.generatedIdField) {
            response.data.forEach((item, index) => {
                item[this.gridStore.selectionStore.generatedIdField] = index + 1;
            });
        }

        return response;
    }

    @action.bound
    onRequestStart() {
        if (this.loadingDelayTime > 0 && !this.loadingTimeoutId) {
            this.loadingTimeoutId = window.setTimeout(() => {
                runInAction(() => {
                    this.spinner = true;
                });
            }, this.loadingDelayTime);
        } else {
            this.spinner = true;
        }

        this.loading = true;

        this.gridStore.notificationStore.closeOutdatedDataMessage();
    }

    @action.bound
    onRequestFinish() {
        clearTimeout(this.loadingTimeoutId);

        this.loadingTimeoutId = null;
        this.loading = false;
        this.spinner = false;
    }
}
